// [syn] screenshot-grade seed — Module 11 downtime/OEE/MTBF/MTTR (paul-tarp.A20 #422).
// v19 M11 §4: "No run-time or breakdown-history data exists anywhere in the
// uploaded set." Every ItemState interval and ReasonCode row below is
// invented to demonstrate the state model and KPI derivation, not read from
// a Bunker Hill document. Item ids reuse the equipment already named in
// `bunker-hill-seed.ts` (CR01, BM01) plus new maintainable-item ids for the
// rest of the grinding→flotation→tailings train.
//
// Basis: 30 days ending at the start of the current (open) production day,
// 2026-06-08T00:00 — "closed production days run to 07 Jun" (brief-a20-common).
// The Downtime tab draws only the trailing 7 days (01–07 Jun); the KPI tiles
// read the full 30-day basis. `assertTiling` below guards that every item's
// intervals exactly tile both windows with no gap or overlap.

import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import { addMinutes, diffMinutes } from '@/versions/v1/shared/clock.derive';

export type DowntimeItemId =
  | 'CR01'
  | 'AF01'
  | 'BM01'
  | 'GP01'
  | 'PB-RGH'
  | 'ZN-RGH'
  | 'TK03'
  | 'GP40';

export const DOWNTIME_ITEMS: readonly { id: DowntimeItemId; label: string }[] = [
  { id: 'CR01', label: 'CR01 Primary crusher' },
  { id: 'AF01', label: 'AF01 Apron feeder' },
  { id: 'BM01', label: 'BM01 Ball mill' },
  { id: 'GP01', label: 'GP01 Cyclone feed pump' },
  { id: 'PB-RGH', label: 'Pb rougher bank' },
  { id: 'ZN-RGH', label: 'Zn rougher bank' },
  { id: 'TK03', label: 'TK03 Tails thickener' },
  { id: 'GP40', label: 'GP40 Paste pump' },
];

export type ItemStateKind =
  | 'running_design'
  | 'running_degraded'
  | 'idle_upstream'
  | 'idle_downstream'
  | 'offline_breakdown'
  | 'offline_shutdown';

export type CauseCategoryId =
  | 'pump-failures' | 'reagent-ph-upsets' | 'electrical-trips' | 'mill-crusher-mechanical'
  | 'cyclone-wear' | 'thickener-paste' | 'instrument-failures' | 'external-supply';
/** durable: site cause taxonomy, entered once (ADR-0012). */
export const CAUSE_CATEGORIES: readonly { id: CauseCategoryId; label: string }[] = [
  { id: 'pump-failures', label: 'Pump failures' },
  { id: 'reagent-ph-upsets', label: 'Reagent & pH upsets' },
  { id: 'electrical-trips', label: 'Electrical trips' },
  { id: 'mill-crusher-mechanical', label: 'Mill & crusher mechanical' },
  { id: 'cyclone-wear', label: 'Cyclone & wear' },
  { id: 'thickener-paste', label: 'Thickener & paste' },
  { id: 'instrument-failures', label: 'Instrument failures' },
  { id: 'external-supply', label: 'External supply' },
];

export type ItemState = {
  itemId: DowntimeItemId;
  state: ItemStateKind;
  start: SeriesTime;
  end: SeriesTime;
  /** Name of the neighbouring item that caused an idle state (v19 M11 §2). */
  neighbour?: string;
  note?: string;
  workOrderId?: string;
  /** Cause taxonomy on every offline interval — required by the guard below. */
  causeCategory?: CauseCategoryId;
  subCause?: string;
};

export const BASIS_END: SeriesTime = '2026-06-08T00:00';
export const BASIS_DAYS = 30;
export const BASIS_START: SeriesTime = addMinutes(BASIS_END, -BASIS_DAYS * 24 * 60);
export const DRAW_DAYS = 7;
export const DRAW_START: SeriesTime = addMinutes(BASIS_END, -DRAW_DAYS * 24 * 60);
export const DRAW_END: SeriesTime = BASIS_END;

type Block = {
  state: ItemStateKind;
  hours: number;
  neighbour?: string;
  note?: string;
  workOrderId?: string;
  causeCategory?: CauseCategoryId;
  subCause?: string;
};

function buildIntervals(itemId: DowntimeItemId, start: SeriesTime, end: SeriesTime, blocks: readonly Block[]): ItemState[] {
  let cursor = start;
  const out: ItemState[] = [];
  for (const block of blocks) {
    const next = addMinutes(cursor, Math.round(block.hours * 60));
    out.push({
      itemId,
      state: block.state,
      start: cursor,
      end: next,
      neighbour: block.neighbour,
      note: block.note,
      workOrderId: block.workOrderId,
      causeCategory: block.causeCategory,
      subCause: block.subCause,
    });
    cursor = next;
  }
  const totalMinutes = diffMinutes(end, start);
  const builtMinutes = diffMinutes(cursor, start);
  if (builtMinutes !== totalMinutes) {
    throw new Error(
      `downtime.seed: ${itemId} blocks cover ${builtMinutes}m, window is ${totalMinutes}m`,
    );
  }
  return out;
}

/** Older 23-day tail (09 May – 01 Jun). The parts are the totals the 30-day
 *  KPI basis needs; their placement is chosen so the tail is a chronology
 *  rather than a pile: one design-running gap between every consecutive pair
 *  of parts, so no two stoppages share a calendar day and the crew and
 *  time-of-day reads downstream have something to read.
 *
 *  A back-to-back `offline_breakdown` pair is one indivisible part — the
 *  transform's `mergeAdjacentSameState` merges it into a single event, so
 *  splitting the pair across the window would fabricate two events where the
 *  KPI basis counts one. Pairs are moved together and kept adjacent.
 *
 *  Gaps are whole minutes by construction: `buildIntervals` rounds every block
 *  and throws unless the rounded minutes tile the window exactly, so the last
 *  gap absorbs the rounding remainder. Deterministic — no `Math.random`.
 *
 *  `gapPhase` is the fixed position this item's window starts at in
 *  `BACKGROUND_GAP_WEIGHTS`. Without it every item's window is proportionate
 *  in the same shape, so eight items' design remainders all break at the same
 *  fraction of the window and the stoppages pile onto one calendar day — the
 *  clustering the interleave exists to remove. The phase is authored per item,
 *  never computed from the clock. */
const BACKGROUND_GAP_WEIGHTS: readonly number[] = [3, 5, 4, 6, 3, 5, 4, 3, 5, 4, 6, 3];
const BACKGROUND_GAP_NOTE = '30-day basis — background interval';

function buildBackground(
  itemId: DowntimeItemId,
  start: SeriesTime,
  end: SeriesTime,
  parts: readonly Block[],
  gapPhase: number,
): ItemState[] {
  const totalMinutes = diffMinutes(end, start);
  const specifiedMinutes = parts.reduce((sum, part) => sum + Math.round(part.hours * 60), 0);
  const designMinutes = totalMinutes - specifiedMinutes;
  if (designMinutes < 0) {
    throw new Error(`downtime.seed: ${itemId} background over-allocated by ${-designMinutes}m`);
  }

  // Group into indivisible parts: an authored back-to-back pair counts once.
  const groups: Block[][] = [];
  for (const part of parts) {
    const previous = groups[groups.length - 1];
    const previousPart = previous?.[previous.length - 1];
    if (
      previous !== undefined &&
      previousPart !== undefined &&
      previousPart.state === 'offline_breakdown' &&
      part.state === 'offline_breakdown'
    ) {
      previous.push(part);
    } else {
      groups.push([part]);
    }
  }

  const weights: number[] = [];
  for (let i = 0; i < groups.length + 1; i += 1) {
    weights.push(BACKGROUND_GAP_WEIGHTS[(gapPhase + i) % BACKGROUND_GAP_WEIGHTS.length]!);
  }
  const weightTotal = weights.reduce((sum, weight) => sum + weight, 0);
  const gaps: number[] = [];
  let allocated = 0;
  for (let i = 0; i < weights.length - 1; i += 1) {
    const gap = Math.floor((designMinutes * weights[i]!) / weightTotal);
    gaps.push(gap);
    allocated += gap;
  }
  gaps.push(designMinutes - allocated);
  if (gaps[gaps.length - 1]! < 0) {
    throw new Error(`downtime.seed: ${itemId} background gaps over-allocate the design remainder`);
  }

  const blocks: Block[] = [];
  for (let i = 0; i < groups.length; i += 1) {
    blocks.push({ state: 'running_design', hours: gaps[i]! / 60, note: BACKGROUND_GAP_NOTE });
    blocks.push(...groups[i]!);
  }
  // The trailing gap carries no note. It runs to DRAW_START, where the drawn
  // week opens with its own `running_design` block, so the transform's
  // `mergeAdjacentSameState` fuses the two into one interval that straddles
  // the boundary and is clipped to the drawn week — a note here would surface
  // on a drawn-week segment's tooltip and describe 01–07 Jun as background.
  blocks.push({ state: 'running_design', hours: gaps[gaps.length - 1]! / 60 });
  return buildIntervals(itemId, start, end, blocks);
}

// ---------------------------------------------------------------------------
// Drawn week (01–07 Jun) — hand-authored for the timeline, ties BM01's two
// named-neighbour idle examples (v19 M11 §2) directly to the brief's demo
// moment, and threads the 04 Jun BM01 trunnion bearing trip through its
// upstream/downstream neighbours (AF01, GP01).
// ---------------------------------------------------------------------------

const WEEK: Record<DowntimeItemId, ItemState[]> = {
  CR01: buildIntervals('CR01', DRAW_START, DRAW_END, [
    { state: 'running_design', hours: 52 },
    { state: 'idle_downstream', hours: 6, neighbour: 'AF01 apron feeder', note: 'Stockpile level high-high — AF01 reclaim below crusher output' },
    { state: 'running_design', hours: 44 },
    { state: 'running_degraded', hours: 8, note: 'Crusher gap drifting wide — throughput below design' },
    { state: 'running_design', hours: 18 },
    { state: 'offline_breakdown', hours: 6, note: 'Oversize rock jam at crusher throat — manual clear', causeCategory: 'mill-crusher-mechanical', subCause: 'Feed blockage' },
    { state: 'running_design', hours: 34 },
  ]),
  AF01: buildIntervals('AF01', DRAW_START, DRAW_END, [
    { state: 'running_design', hours: 36 },
    { state: 'idle_upstream', hours: 4, neighbour: 'CR01 primary crusher', note: 'Stockpile level low-low — crusher behind' },
    { state: 'running_design', hours: 38 },
    { state: 'idle_downstream', hours: 3.5, neighbour: 'BM01 ball mill', note: 'Fine ore bin high-high — BM01 offline on trunnion bearing trip' },
    { state: 'running_design', hours: 40.5 },
    { state: 'offline_shutdown', hours: 4, note: 'Feeder belt inspection — paired with BM01 liner bolt check', causeCategory: 'mill-crusher-mechanical', subCause: 'Belt inspection' },
    { state: 'running_design', hours: 38 },
    { state: 'running_degraded', hours: 4, note: 'Belt slip — reduced reclaim rate' },
  ]),
  BM01: buildIntervals('BM01', DRAW_START, DRAW_END, [
    { state: 'running_design', hours: 28 },
    { state: 'idle_upstream', hours: 2, neighbour: 'fine ore bin', note: 'Fine ore bin level low-low' },
    { state: 'running_design', hours: 48 },
    { state: 'offline_breakdown', hours: 3.5, note: 'Trunnion bearing temperature trip — DE bearing HH interlock', workOrderId: 'WO-441', causeCategory: 'mill-crusher-mechanical', subCause: 'Mill mechanical trip' },
    { state: 'running_degraded', hours: 16, note: 'Extended post-repair ramp — grinding rate held below design while the trunnion bearing repair is proven out' },
    { state: 'running_design', hours: 8 },
    // [syn] June mill stop reconciles the closed-day M4 availability to Planning's 95.2%.
    { state: 'offline_breakdown', hours: 4.5, note: '[syn] Mill lubrication inspection after the trunnion bearing repair', causeCategory: 'mill-crusher-mechanical', subCause: 'Mill lubrication inspection' },
    { state: 'running_design', hours: 12 },
    { state: 'offline_shutdown', hours: 4, note: 'Planned liner bolt torque check', causeCategory: 'mill-crusher-mechanical', subCause: 'Liner inspection' },
    { state: 'running_design', hours: 26 },
    { state: 'idle_downstream', hours: 1.5, neighbour: 'cyclone pump box', note: 'Cyclone pump box level high-high' },
    { state: 'running_design', hours: 14.5 },
  ]),
  GP01: buildIntervals('GP01', DRAW_START, DRAW_END, [
    { state: 'running_design', hours: 78 },
    { state: 'idle_upstream', hours: 3.5, neighbour: 'BM01 ball mill', note: 'Ball mill discharge offline — BM01 trunnion bearing breakdown' },
    { state: 'running_degraded', hours: 20, note: 'Cyclone feed held below design while BM01 runs its extended post-repair ramp' },
    { state: 'running_design', hours: 57.5 },
    { state: 'idle_downstream', hours: 2, neighbour: 'Pb rougher bank', note: 'Flotation feed sump level high-high' },
    { state: 'running_design', hours: 7 },
  ]),
  'PB-RGH': buildIntervals('PB-RGH', DRAW_START, DRAW_END, [
    { state: 'running_design', hours: 54 },
    { state: 'running_degraded', hours: 10, note: 'Rougher air flow off design' },
    { state: 'running_design', hours: 40 },
    { state: 'idle_downstream', hours: 8, neighbour: 'Pb cleaner circuit', note: 'Pb cleaner feed sump level high-high' },
    { state: 'running_design', hours: 38 },
    { state: 'offline_breakdown', hours: 10, note: 'Rougher air blower motor tripped on overload', causeCategory: 'electrical-trips', subCause: 'Motor protection trip' },
    { state: 'running_design', hours: 8 },
  ]),
  'ZN-RGH': buildIntervals('ZN-RGH', DRAW_START, DRAW_END, [
    { state: 'running_design', hours: 32 },
    { state: 'idle_upstream', hours: 4, neighbour: 'Zn conditioning tank', note: 'Zn conditioning tank level low — lime dosing interruption' },
    { state: 'running_design', hours: 54 },
    { state: 'running_degraded', hours: 8, note: 'Zn float pH off design' },
    { state: 'running_design', hours: 42 },
    { state: 'idle_downstream', hours: 6, neighbour: 'Zn cleaner circuit', note: 'Zn cleaner feed sump level high-high' },
    { state: 'running_design', hours: 22 },
  ]),
  TK03: buildIntervals('TK03', DRAW_START, DRAW_END, [
    { state: 'running_design', hours: 60 },
    { state: 'idle_upstream', hours: 4, neighbour: 'rougher tails header', note: 'Rougher tails flow low — plant throughput dip' },
    { state: 'running_design', hours: 66 },
    { state: 'offline_shutdown', hours: 4, note: 'Rake mechanism planned inspection', causeCategory: 'thickener-paste', subCause: 'Rake drive inspection' },
    { state: 'running_design', hours: 28 },
    { state: 'idle_downstream', hours: 4, neighbour: 'GP40 paste pump', note: 'Paste plant feed high — GP40 running below rate' },
    { state: 'running_design', hours: 2 },
  ]),
  GP40: buildIntervals('GP40', DRAW_START, DRAW_END, [
    { state: 'running_design', hours: 44 },
    { state: 'running_degraded', hours: 8, note: 'Paste rheology thick — reduced pump rate' },
    { state: 'running_design', hours: 106 },
    { state: 'offline_breakdown', hours: 4, note: 'Paste pump seal failure', causeCategory: 'pump-failures', subCause: 'Gland seal failure' },
    { state: 'running_design', hours: 6 },
  ]),
};

// ---------------------------------------------------------------------------
// Older 23-day tail — totals only, so the 30-day KPI basis has enough
// breakdown events for a meaningful MTBF/MTTR without hand-authoring a full
// month of chronology. `buildBackground` places these parts across the window;
// the notes below are what the interval carries once it is placed.
// ---------------------------------------------------------------------------

const BACKGROUND: Record<DowntimeItemId, ItemState[]> = {
  CR01: buildBackground('CR01', BASIS_START, DRAW_START, [
    { state: 'running_degraded', hours: 12 },
    { state: 'idle_downstream', hours: 10, neighbour: 'AF01 apron feeder' },
    { state: 'offline_breakdown', hours: 6, note: 'Mantle liner wear — crusher chamber opened and liner rotated', causeCategory: 'mill-crusher-mechanical', subCause: 'Crusher liner wear' },
    { state: 'offline_breakdown', hours: 5, note: 'Mantle liner wear — crusher chamber opened and liner rotated', causeCategory: 'mill-crusher-mechanical', subCause: 'Crusher liner wear' },
  ], 5),
  AF01: buildBackground('AF01', BASIS_START, DRAW_START, [
    { state: 'running_degraded', hours: 10 },
    { state: 'idle_upstream', hours: 8, neighbour: 'CR01 primary crusher' },
    { state: 'idle_downstream', hours: 6, neighbour: 'BM01 ball mill' },
    { state: 'offline_breakdown', hours: 4, note: 'Site supply dip — feeder drive tripped on undervoltage, restarted once the supply held steady', causeCategory: 'external-supply', subCause: 'Supply undervoltage' },
  ], 4),
  BM01: buildBackground('BM01', BASIS_START, DRAW_START, [
    { state: 'running_degraded', hours: 16 },
    { state: 'idle_upstream', hours: 14, neighbour: 'fine ore bin' },
    { state: 'idle_downstream', hours: 14, neighbour: 'cyclone pump box' },
    { state: 'offline_shutdown', hours: 8, note: 'Planned mill reline — shell liner rotation', causeCategory: 'mill-crusher-mechanical', subCause: 'Planned reline' },
    { state: 'offline_breakdown', hours: 9, note: 'Mill motor protection relay tripped — stator winding fault confirmed by the electrical team', causeCategory: 'electrical-trips', subCause: 'Motor protection trip' },
    { state: 'offline_breakdown', hours: 4, note: 'Mill motor protection relay tripped — stator winding fault confirmed by the electrical team', causeCategory: 'electrical-trips', subCause: 'Motor protection trip' },
  ], 2),
  GP01: buildBackground('GP01', BASIS_START, DRAW_START, [
    { state: 'running_degraded', hours: 8 },
    { state: 'idle_upstream', hours: 12, neighbour: 'BM01 ball mill' },
    { state: 'idle_downstream', hours: 6, neighbour: 'Pb rougher bank' },
    { state: 'offline_breakdown', hours: 5, note: 'Cyclone spigot wear — spigot replaced on the worst two cyclones', causeCategory: 'cyclone-wear', subCause: 'Cyclone spigot wear' },
  ], 1),
  'PB-RGH': buildBackground('PB-RGH', BASIS_START, DRAW_START, [
    { state: 'running_degraded', hours: 14 },
    { state: 'idle_downstream', hours: 10, neighbour: 'Pb cleaner circuit' },
    { state: 'offline_breakdown', hours: 2, note: 'Rougher pH controller reading off scale — probe cleaned and recalibrated', causeCategory: 'instrument-failures', subCause: 'pH probe fault' },
    { state: 'offline_breakdown', hours: 6, note: 'Rougher pH controller reading off scale — probe cleaned and recalibrated', causeCategory: 'instrument-failures', subCause: 'pH probe fault' },
  ], 7),
  'ZN-RGH': buildBackground('ZN-RGH', BASIS_START, DRAW_START, [
    { state: 'running_degraded', hours: 10 },
    { state: 'idle_upstream', hours: 6, neighbour: 'Zn conditioning tank' },
    { state: 'idle_downstream', hours: 8, neighbour: 'Zn cleaner circuit' },
    { state: 'offline_breakdown', hours: 6, note: 'Lime dosing pump discharge blocked — line flushed and pump overhauled', causeCategory: 'reagent-ph-upsets', subCause: 'Lime dosing blockage' },
  ], 3),
  TK03: buildBackground('TK03', BASIS_START, DRAW_START, [
    { state: 'idle_upstream', hours: 8, neighbour: 'rougher tails header' },
    { state: 'offline_shutdown', hours: 6, note: 'Planned thickener rake drive inspection', causeCategory: 'thickener-paste', subCause: 'Rake drive inspection' },
    { state: 'offline_breakdown', hours: 5, note: 'Tails thickener underflow line ruptured — section replaced', causeCategory: 'thickener-paste', subCause: 'Underflow line rupture' },
  ], 4),
  GP40: buildBackground('GP40', BASIS_START, DRAW_START, [
    { state: 'running_degraded', hours: 10 },
    { state: 'offline_breakdown', hours: 4, note: 'Paste pump gland seal weeping — seal kit replaced', causeCategory: 'pump-failures', subCause: 'Gland seal failure' },
    { state: 'offline_breakdown', hours: 3, note: 'Paste pump gland seal weeping — seal kit replaced', causeCategory: 'pump-failures', subCause: 'Gland seal failure' },
  ], 11)
};

export const DOWNTIME_STATES: readonly ItemState[] = DOWNTIME_ITEMS.flatMap(
  ({ id }) => [...BACKGROUND[id], ...WEEK[id]],
);

// Fail closed, at module load, on any offline interval that reaches a
// downstream reader uncoded. There is no silent default category: a missing
// code is a seed defect, and a default would make every Pareto and reliability
// read below it quietly wrong.
for (const row of DOWNTIME_STATES) {
  const offline = row.state === 'offline_breakdown' || row.state === 'offline_shutdown';
  if (offline) {
    if (row.causeCategory === undefined) {
      throw new Error(`downtime.seed: ${row.itemId} ${row.start} uncoded`);
    }
    if (row.subCause === undefined || row.subCause.trim().length === 0) {
      throw new Error(`downtime.seed: ${row.itemId} ${row.start} uncoded`);
    }
  } else if (row.causeCategory !== undefined || row.subCause !== undefined) {
    throw new Error(`downtime.seed: ${row.itemId} ${row.start} ${row.state} carries a cause`);
  }
}

// ---------------------------------------------------------------------------
// Reason codes — the human-in-the-loop coding layer (v19 M11 §1, §3). Every
// row cites a non-running interval from WEEK above. Two rows are pending the
// reliability engineer's review; one has been recoded Operational → Mechanical
// after the reliability engineer traced the crusher jam to liner wear.
// ---------------------------------------------------------------------------

export type ReasonCodeCategory = 'Operational' | 'Mechanical' | 'Electrical';
export type ReasonCodeReviewStatus = 'pending' | 'confirmed' | 'recoded';

export type ReasonCodeEvent = {
  id: string;
  itemId: DowntimeItemId;
  state: ItemStateKind;
  start: SeriesTime;
  end: SeriesTime;
  code: string;
  description: string;
  category: ReasonCodeCategory;
  enteredBy: string;
  reviewStatus: ReasonCodeReviewStatus;
  recodedFrom?: ReasonCodeCategory;
  reviewedBy?: string;
};

/** D. Okafor holds `seat-cro` (bunker-hill-org-seed.ts) — the named control
 *  room operator who codes every downtime/idle event this week (v19 M11 §1). */
const CRO = 'D. Okafor';
const RELIABILITY_ENGINEER = 'Reliability engineer'; // seat-sre — no named incumbent yet

export const REASON_CODES: readonly ReasonCodeEvent[] = [
  {
    id: 'rc-1',
    itemId: 'BM01',
    state: 'offline_breakdown',
    start: '2026-06-04T06:00',
    end: '2026-06-04T09:30',
    code: 'MECH-BRG-HOT',
    description: 'Trunnion bearing temperature trip — DE bearing >90 °C, mill tripped on HH interlock (WO-441).',
    category: 'Mechanical',
    enteredBy: CRO,
    reviewStatus: 'confirmed',
    reviewedBy: RELIABILITY_ENGINEER,
  },
  {
    id: 'rc-2',
    itemId: 'BM01',
    state: 'idle_upstream',
    start: '2026-06-02T04:00',
    end: '2026-06-02T06:00',
    code: 'IDLE-FOB-LL',
    description: 'Fine ore bin level low-low — feed interruption from AF01.',
    category: 'Operational',
    enteredBy: CRO,
    reviewStatus: 'confirmed',
    reviewedBy: RELIABILITY_ENGINEER,
  },
  {
    id: 'rc-3',
    itemId: 'BM01',
    state: 'idle_downstream',
    start: '2026-06-07T08:00',
    end: '2026-06-07T09:30',
    code: 'IDLE-CPB-HH',
    description: 'Cyclone pump box level high-high — GP01 backed up.',
    category: 'Operational',
    enteredBy: CRO,
    reviewStatus: 'pending',
  },
  {
    id: 'rc-4',
    itemId: 'BM01',
    state: 'offline_shutdown',
    start: '2026-06-06T02:00',
    end: '2026-06-06T06:00',
    code: 'PLND-LINER',
    description: 'Planned liner bolt torque check.',
    category: 'Operational',
    enteredBy: CRO,
    reviewStatus: 'confirmed',
    reviewedBy: RELIABILITY_ENGINEER,
  },
  {
    id: 'rc-5',
    itemId: 'CR01',
    state: 'offline_breakdown',
    start: '2026-06-06T08:00',
    end: '2026-06-06T14:00',
    code: 'MECH-JAM',
    description: 'Oversize rock jam at crusher throat — traced to liner wear on review, not the operational stoppage first logged.',
    category: 'Mechanical',
    enteredBy: CRO,
    reviewStatus: 'recoded',
    recodedFrom: 'Operational',
    reviewedBy: RELIABILITY_ENGINEER,
  },
  {
    id: 'rc-6',
    itemId: 'CR01',
    state: 'idle_downstream',
    start: '2026-06-03T04:00',
    end: '2026-06-03T10:00',
    code: 'IDLE-STK-HH',
    description: 'Stockpile level high-high — AF01 reclaim rate below crusher output.',
    category: 'Operational',
    enteredBy: CRO,
    reviewStatus: 'confirmed',
    reviewedBy: RELIABILITY_ENGINEER,
  },
  {
    id: 'rc-9',
    itemId: 'PB-RGH',
    state: 'offline_breakdown',
    start: '2026-06-07T06:00',
    end: '2026-06-07T16:00',
    code: 'ELEC-BLWR-TRIP',
    description: 'Rougher air blower motor tripped on overload — VSD fault.',
    category: 'Electrical',
    enteredBy: CRO,
    reviewStatus: 'pending',
  },
];
